import React, { useContext, useMemo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import { AiMenuWidget } from '../../components/AiSuggestions';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { FEATURES } from '../../constants/featureAccess';
import { FeatureLockCard } from '../../components/FeatureGate';
import '../../css/homePages.css';
import '../../css/aiSuggestions.css';
import { fetchWithAuth } from '../../utils/authUtils';
import { copyToClipboard } from '../../utils/copyToClipboard';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const HomeElderly = () => {
    const { data, accessToken, authReady } = useContext(DataContext);
    const { canUse } = useFeatureAccess();
    const canCheckin = canUse(FEATURES.DAILY_CHECKIN);
    const canEmergency = canUse(FEATURES.EMERGENCY_CALL);
    const canAiNutrition = canUse(FEATURES.AI_NUTRITION);
    const navigate = useNavigate();

    const myKey = useMemo(() => {
        return data?.linkKey || 'Chưa có mã';
    }, [data]);

    const [updatedToday, setUpdatedToday] = useState(false);
    const [checkedInToday, setCheckedInToday] = useState(false);
    const [loadingCheckinStatus, setLoadingCheckinStatus] = useState(true);
    const [submittingCheckin, setSubmittingCheckin] = useState(false);
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderSent, setReminderSent] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const profileId = data?.profileId;

    useEffect(() => {
        const loadReminderStatus = async () => {
            if (!canCheckin) return;
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/alerts/checkin-reminder-status`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success) {
                    setReminderSent(payload.data?.sentToday || false);
                }
            } catch (_) {}
        };
        if (authReady && accessToken && data?.id && String(data?.role).toLowerCase() === 'elderly' && canCheckin) {
            loadReminderStatus();
        }
    }, [authReady, accessToken, data?.id, data?.role, canCheckin]);

    const handleReminderClick = async () => {
        try {
            setSendingReminder(true);
            const res = await fetchWithAuth(`${API_BASE}/api/alerts/checkin-reminder`, {
                method: 'POST'
            });
            const payload = await res.json().catch(() => ({}));
            if (res.ok && payload?.success) {
                setReminderSent(true);
                setModalMessage('Đã gửi nhắc nhở cho người thân');
                setShowModal(true);
            } else {
                setModalMessage(payload?.message || 'Không thể gửi nhắc nhở');
                setShowModal(true);
            }
        } catch (error) {

            setModalMessage('Lỗi khi gửi nhắc nhở');
            setShowModal(true);
        } finally {
            setSendingReminder(false);
        }
    };

    useEffect(() => {
        const loadDailyCheckinStatus = async () => {
            if (!canCheckin) {
                setLoadingCheckinStatus(false);
                return;
            }
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/daily-checkin/status`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && payload?.data) {
                    setCheckedInToday(Boolean(payload.data.checkedInToday));
                }
            } catch (_) {
            } finally {
                setLoadingCheckinStatus(false);
            }
        };
        if (authReady && accessToken && data?.id && String(data?.role).toLowerCase() === 'elderly') {
            if (canCheckin) {
                loadDailyCheckinStatus();
            } else {
                setLoadingCheckinStatus(false);
            }
        }
    }, [authReady, accessToken, data?.id, data?.role, canCheckin]);

    const handleDailyCheckin = async () => {
        if (checkedInToday || submittingCheckin) return;
        try {
            setSubmittingCheckin(true);
            const res = await fetchWithAuth(`${API_BASE}/api/daily-checkin`, { method: 'POST' });
            const payload = await res.json().catch(() => ({}));
            if (res.ok && payload?.success) {
                setCheckedInToday(Boolean(payload.data?.checkedInToday ?? true));
                setModalMessage(payload.message || 'Điểm danh thành công!');
                setShowModal(true);
            } else {
                setModalMessage(payload?.message || 'Không thể điểm danh');
                setShowModal(true);
            }
        } catch (error) {
            setModalMessage('Lỗi khi điểm danh');
            setShowModal(true);
        } finally {
            setSubmittingCheckin(false);
        }
    };

    useEffect(() => {
        const loadStatus = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/health-logs/today-status`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && Array.isArray(payload?.data)) {
                    const mine = payload.data.find((x) => Number(x.id) === Number(data?.id));
                    setUpdatedToday(Boolean(mine?.updatedToday));
                }
            } catch (_) {}
        };
        if (authReady && accessToken && data?.id) loadStatus();
    }, [authReady, accessToken, data?.id]);

    return (
        <div className="tamcare-home-page">
            <div className="tamcare-home-shell">
                <div className="tamcare-connection-pill tamcare-fade-up">
                    <i className="fa-solid fa-key fa-2x"></i>
                    <span style={{ fontWeight: 600 }}>Mã kết nối của bác:</span>
                    <strong className="tamcare-connection-key">{myKey}</strong>
                    {myKey !== 'Chưa có mã' && (
                        <button
                            type="button"
                            className="tamcare-copy-btn"
                            onClick={async () => {
                                try {
                                    await copyToClipboard(String(myKey));
                                } catch (error) {
                                    // ignore
                                }
                            }}
                        >
                            Sao chép mã
                        </button>
                    )}
                </div>

                <section className="tamcare-hero-banner tamcare-fade-up">
                    <h1>CHÚC BÁC MỘT NGÀY TỐT LÀNH!</h1>
                    <p>Bác nhớ điểm danh mỗi ngày để gia đình yên tâm nhé ạ</p>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                        {canCheckin ? (
                            <button
                                className="tamcare-btn-checkin"
                                type="button"
                                onClick={handleDailyCheckin}
                                disabled={loadingCheckinStatus || submittingCheckin || checkedInToday}
                            >
                                <i className="fa-solid fa-clipboard-check"></i>{' '}
                                {loadingCheckinStatus
                                    ? 'Đang tải...'
                                    : submittingCheckin
                                        ? 'Đang điểm danh...'
                                        : checkedInToday
                                            ? 'ĐÃ ĐIỂM DANH HÔM NAY'
                                            : 'Điểm danh ngay'}
                            </button>
                        ) : (
                            <Link to="/membership" className="tamcare-btn-checkin" style={{ textDecoration: 'none', opacity: 0.85 }}>
                                <i className="fa-solid fa-lock"></i> Điểm danh (Pro)
                            </Link>
                        )}
                        {canEmergency ? (
                            <a href="tel:115" className="tamcare-btn-sos tamcare-fade-up" style={{ animationDelay: '0.5s', marginTop: 10, marginBottom: 0, alignSelf: 'center', padding: '12px 22px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fa-solid fa-phone-volume"></i>
                                <span style={{ marginLeft: 8 }}>Gọi khẩn cấp</span>
                            </a>
                        ) : (
                            <Link to="/membership" className="tamcare-btn-sos tamcare-fade-up" style={{ animationDelay: '0.5s', marginTop: 10, marginBottom: 0, alignSelf: 'center', padding: '12px 22px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', opacity: 0.85 }}>
                                <i className="fa-solid fa-lock"></i>
                                <span style={{ marginLeft: 8 }}>Gọi khẩn cấp (Pro)</span>
                            </Link>
                        )}
                    </div>
                </section>

                <div className="tamcare-info-grid">
                    <article className="tamcare-big-card tamcare-fade-up" style={{ animationDelay: '0.2s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h2 className="tamcare-card-title" style={{ marginBottom: 0 }}><i className="fa-solid fa-newspaper"></i> Mẹo Sống Khỏe</h2>
                            <Link to="/blogs" style={{ fontSize: 20, color: '#2c5282', fontWeight: 700, textDecoration: 'none' }}>Xem tất cả &gt;</Link>
                        </div>
                        <div className="tamcare-suggestion-row">
                            <img
                                src="https://img.freepik.com/free-vector/elderly-health-concept-illustration_114360-6023.jpg"
                                alt="Mẹo sống khỏe"
                            />
                            <div className="tamcare-card-text">
                                <strong style={{ color: '#1e293b' }}>Giữ ấm cơ thể</strong>
                                <br />
                                Bác nhớ quàng khăn và đi tất ấm mỗi sáng sớm để bảo vệ sức khỏe...
                            </div>
                        </div>
                    </article>

                    <article className="tamcare-big-card tamcare-big-card--nutrition tamcare-fade-up" style={{ animationDelay: '0.3s' }}>
                        {canAiNutrition ? (
                            <AiMenuWidget elderlyId={data?.id} showTitle={true} />
                        ) : (
                            <FeatureLockCard featureKey={FEATURES.AI_NUTRITION} compact />
                        )}
                    </article>

                    <article className="tamcare-big-card tamcare-big-card--medical tamcare-fade-up" style={{ animationDelay: '0.4s' }}>
                        <h2 style={{ color: '#f39c12' }}><i className="fa-solid fa-notes-medical"></i> Hồ sơ bệnh án</h2>
                        <div className="tamcare-card-text">
                            <p style={{ marginTop: 0 }}>
                                Lưu lại những bệnh đã từng mắc và tình trạng sức khỏe hiện tại để con cháu và bác sĩ tiện theo dõi.
                            </p>
                            {profileId ? (
                                <Link to={`/medical-history/${profileId}`} style={{ display: 'inline-block', marginTop: 10, padding: '12px 22px', borderRadius: 999, background: '#f39c12', color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}>
                                    Xem / cập nhật hồ sơ
                                </Link>
                            ) : (
                                <Link to="/medical-history" style={{ display: 'inline-block', marginTop: 10, padding: '12px 22px', borderRadius: 999, background: '#f39c12', color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}>
                                    Xem hồ sơ sức khỏe
                                </Link>
                            )}
                        </div>
                    </article>

                    <article className="tamcare-big-card tamcare-big-card--medical tamcare-fade-up" style={{ animationDelay: '0.45s' }}>
                        <h2 style={{ color: '#0f766e' }}><i className="fa-solid fa-heart-pulse"></i> Tình trạng sức khỏe</h2>
                        <div className="tamcare-card-text">
                            <p style={{ marginTop: 0 }}>
                                Ghi nhận huyết áp, tim mạch, đường huyết, nhiệt độ và cân nặng mỗi ngày.
                            </p>
                            <Link to="/health" style={{ display: 'inline-block', marginTop: 10, padding: '12px 22px', borderRadius: 999, background: '#0f766e', color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}>
                                Xem chỉ số
                            </Link>
                        </div>
                    </article>

                    <article className="tamcare-big-card tamcare-big-card--medication tamcare-fade-up" style={{ animationDelay: '0.5s' }}>
                        <h2 style={{ color: '#8b5cf6' }}><i className="fa-solid fa-calendar-days"></i> Lịch chăm sóc</h2>
                        <div className="tamcare-card-text">
                            <p style={{ marginTop: 0 }}>
                                Lịch nhắc các nhiệm vụ chăm sóc và theo dõi hoàn thành trong ngày của bác.
                            </p>
                            <Link to={`/medication-checklist/${data?.id}`} style={{ display: 'inline-block', marginTop: 10, padding: '12px 22px', borderRadius: 999, background: '#8b5cf6', color: '#ffffff', fontWeight: 700, textDecoration: 'none' }}>
                                Xem lịch
                            </Link>
                        </div>
                    </article>

                </div>

                <section className="tamcare-map-section tamcare-fade-up" style={{ animationDelay: '0.35s', marginBottom: 24 }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                        <button
                            className="tamcare-btn-checkin"
                            type="button"
                            style={{ marginTop: 0 }}
                            onClick={() => (updatedToday ? navigate('/health') : (reminderSent ? null : handleReminderClick()))}
                            disabled={sendingReminder || reminderSent}
                        >
                            <i className="fa-solid fa-heart-pulse"></i>{' '}
                            {sendingReminder ? 'Đang gửi...' : (updatedToday ? 'ĐÃ CẬP NHẬT HÔM NAY' : (reminderSent ? 'Đã nhắc nhở cập nhật chỉ số hôm nay' : 'Nhắc cập nhật chỉ số hôm nay'))}
                        </button>
                    </div>
                </section>

                <section className="tamcare-map-section tamcare-fade-up" style={{ animationDelay: '0.4s' }}>
                    <h2 className="tamcare-map-title"><i className="fa-solid fa-map-location-dot"></i> Vị trí</h2>
                    <div className="tamcare-map-wrapper">
                        <iframe
                            title="tamcare-location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3724.4855345754974!2d105.5248756113887!3d21.013244988225573!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31345b465a4e65fb%3A0xaae60567ad2213d0!2zS2h1IEPDtG5nIG5naOG7hyBDYW8gSMOyYSBM4bqhYw!5e0!3m2!1svi!2s!4v1710520000000!5m2!1svi!2s"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                        />
                    </div>
                </section>

                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '30px',
                            borderRadius: '12px',
                            maxWidth: '400px',
                            width: '90%',
                            textAlign: 'center',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#333' }}>{modalMessage}</p>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    backgroundColor: '#0f766e',
                                    color: '#fff',
                                    border: 'none',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HomeElderly;